import { useEffect } from "react"
import { View, ViewStyle } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image, ImageStyle } from "expo-image"

import { useQueuedVideoThumbnail } from "src/context/ThumbnailQueueContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

// Fallback design: outer rectangle, inner rectangle (matching rounded corners), centered play arrow
const FALLBACK_BORDER_RADIUS = 12
const FALLBACK_INSET = 55
const FALLBACK_INNER_RADIUS = FALLBACK_BORDER_RADIUS

interface VideoThumbnailProps {
  videoUrl: string
  thumbnailUrl?: string // Optional separate URL for thumbnail (when videoUrl is HLS)
  /** When set, use this URL directly as the image; skip local thumbnail generation. */
  backendThumbnailUrl?: string
  /**
   * Called when the thumbnail cannot be shown — either because the backend-provided
   * image URL failed to load (e.g. expired CloudFront signed URL) or because local
   * thumbnail generation from the video URL also failed (e.g. expired HLS URL).
   * The parent can use this to fetch fresh URLs via the content resource API.
   */
  onThumbnailError?: () => void
  /**
   * Priority for thumbnail generation queue.
   * Higher values are processed first. Use message timestamp for recency-based ordering.
   * Defaults to 0 (lowest priority).
   */
  priority?: number
}

export const VideoThumbnail = ({
  videoUrl,
  thumbnailUrl,
  backendThumbnailUrl,
  onThumbnailError,
  priority = 0,
}: VideoThumbnailProps) => {
  const { themed } = useAppTheme()
  const { thumbnailUri, isError } = useQueuedVideoThumbnail(
    thumbnailUrl || videoUrl,
    priority,
    backendThumbnailUrl,
  )

  // Fire the error callback when local thumbnail generation fails (no backendThumbnailUrl
  // was available to display, so the queue attempted to generate one from the video URL
  // and that also failed — typically an expired signed HLS URL).
  useEffect(() => {
    if (isError && !backendThumbnailUrl) {
      onThumbnailError?.()
    }
  }, [isError, backendThumbnailUrl, onThumbnailError])

  // Define a stroke style for the play icon
  const $iconStroke: ViewStyle = {
    borderRadius: 25,
    backgroundColor: "#000",
  }

  return (
    <View style={themed($container)}>
      <View style={$imagePlaceholder}>
        {thumbnailUri && (
          <Image
            source={{ uri: thumbnailUri }}
            style={$image}
            recyclingKey={`video-thumbnail-${videoUrl}`}
            onError={() => {
              // Only propagate errors from the backend-provided URL — locally generated
              // thumbnails don't have a refresh path.
              if (backendThumbnailUrl) {
                onThumbnailError?.()
              }
            }}
          />
        )}
        {/* Fallback: outer rectangle, inner rectangle (matching rounded corners), centered play arrow */}
        {!thumbnailUri && (
          <View style={themed($fallbackOuter)}>
            <View style={themed($fallbackInner)}>
              <MaterialIcons name="play-arrow" size={64} color="#FFFFFF" />
            </View>
          </View>
        )}
      </View>
      <View style={[themed($videoIcon), $iconStroke]}>
        <MaterialIcons name="play-circle-filled" size={50} color="white" />
      </View>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginBottom: spacing.sm,
  maxWidth: 300,
})

const $imagePlaceholder: ViewStyle = {
  width: "100%",
  height: 180,
  backgroundColor: "#888",
  borderRadius: FALLBACK_BORDER_RADIUS,
  overflow: "hidden",
}

const $image: ImageStyle = {
  width: "100%",
  height: 180,
  borderRadius: FALLBACK_BORDER_RADIUS,
}

const $videoIcon: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  position: "absolute",
  bottom: spacing.sm,
  right: spacing.sm,
})

const $fallbackOuter: ThemedStyle<ViewStyle> = ({ colors }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: colors.palette.neutral750,
  borderRadius: FALLBACK_BORDER_RADIUS,
  padding: FALLBACK_INSET,
  justifyContent: "center",
  alignItems: "center",
})

const $fallbackInner: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  width: "100%",
  backgroundColor: colors.palette.neutral775,
  borderRadius: FALLBACK_INNER_RADIUS,
  justifyContent: "center",
  alignItems: "center",
})
